import React, { useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import type { TagSpan } from '../../../utils/promptMentions';

export interface HighlightSpan extends TagSpan {
    /** It names a reference that is attached */
    known: boolean;
}

interface PromptHighlightsProps {
    /** The prompt field the highlights lie under */
    fieldRef: React.RefObject<HTMLTextAreaElement>;
    value: string;
    spans: HighlightSpan[];
    /** The tag whose menu is open */
    activeStart?: number | null;
}

/** What decides where the field's text falls: copied, so the highlights wrap and line up as it does */
const COPIED = [
    'fontFamily', 'fontSize', 'fontWeight', 'fontStyle', 'fontVariant', 'fontStretch', 'fontKerning', 'fontFeatureSettings', 'fontVariationSettings',
    'textRendering', 'lineHeight', 'letterSpacing', 'wordSpacing',
    'textTransform', 'textIndent', 'tabSize', 'whiteSpace', 'overflowWrap', 'wordBreak',
    'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
    'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth',
] as const;

/**
 * The tags of a prompt marked in place: a layer behind the prompt field (which has no background
 * of its own), holding the same text, invisible, with a soft pill behind each tag. The field's
 * own text, caret and selection stay as they are; the layer follows the field's size and scroll.
 */
export const PromptHighlights = React.forwardRef<HTMLDivElement | null, PromptHighlightsProps>(({ fieldRef, value, spans, activeStart = null }, ref) => {
    const boxRef = useRef<HTMLDivElement>(null);
    const [retry, setRetry] = useState(0);
    useImperativeHandle(ref, () => boxRef.current as HTMLDivElement, []);

    useLayoutEffect(() => {
        const field = fieldRef.current;
        const box = boxRef.current;
        if (!box) return;
        // A field rendered after the layer gets its ref only after this runs: placed a frame later then
        if (!field) {
            const frame = requestAnimationFrame(() => setRetry(n => n + 1));
            return () => cancelAnimationFrame(frame);
        }
        const place = () => {
            const style = getComputedStyle(field);
            for (const key of COPIED) box.style[key] = style[key];
            const borderX = (parseFloat(style.borderLeftWidth) || 0) + (parseFloat(style.borderRightWidth) || 0);
            const borderY = (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
            box.style.left = `${field.offsetLeft}px`;
            box.style.top = `${field.offsetTop}px`;
            // The field's text width leaves out a scrollbar, so the layer's does too
            box.style.width = `${field.clientWidth + borderX}px`;
            box.style.height = `${field.clientHeight + borderY}px`;
            box.scrollTop = field.scrollTop;
        };
        place();
        const onScroll = () => { box.scrollTop = field.scrollTop; };
        field.addEventListener('scroll', onScroll);
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(place) : null;
        observer?.observe(field);
        return () => { field.removeEventListener('scroll', onScroll); observer?.disconnect(); };
    }, [fieldRef, value, retry]);

    const parts: React.ReactNode[] = [];
    let at = 0;
    for (const span of spans) {
        if (span.start < at) continue;
        if (span.start > at) parts.push(value.slice(at, span.start));
        const active = activeStart === span.start;
        parts.push(
            <mark
                key={span.start}
                className="rounded-sm text-transparent"
                style={{
                    background: span.known ? (active ? 'var(--tone-line)' : 'var(--tone-soft)') : 'var(--danger-soft)',
                    boxShadow: `0 0 0 1.5px ${span.known ? (active ? 'var(--tone-deep)' : 'var(--tone-line)') : 'var(--danger)'}`,
                }}
                data-tag-start={span.start}
                data-tag-known={span.known ? '1' : '0'}
            >
                {value.slice(span.start, span.end)}
            </mark>,
        );
        at = span.end;
    }
    if (at < value.length) parts.push(value.slice(at));
    // A last empty line still takes its height
    if (value.endsWith('\n')) parts.push(' ');

    return (
        <div
            ref={boxRef}
            aria-hidden="true"
            className="absolute z-0 overflow-hidden pointer-events-none select-none text-transparent border-solid border-transparent box-border"
            data-prompt-highlights
        >
            {parts}
        </div>
    );
});

PromptHighlights.displayName = 'PromptHighlights';

export default PromptHighlights;
